import {
	contentLocalizationCreateSchema,
	contentLocalizationUpdateSchema,
	contentVersionCreateSchema,
} from '@today/models'
import {
	ContentBodyFormat,
	type ContentLocalizationSummaryDTO,
	type ContentTranslationOrigin,
	type ContentVersionDTO,
} from '@today/types'
import { schemaErrors } from './schema-check'

/** Versions, language tabs and drafts (TO-420 content-admin-ui REQ-004). */

export interface DraftValues {
	title: string
	body: string
}

export interface AddLanguageValues {
	locale: string
	origin: ContentTranslationOrigin.MANUAL | ContentTranslationOrigin.IMPORT
	translatedFromId: string
	title: string
	body: string
}

/** The source language first, then the other localizations ascending. */
export const orderedLocales = <T extends Pick<ContentLocalizationSummaryDTO, 'locale'>>(
	localizations: readonly T[],
	sourceLocale: string,
): T[] =>
	[...localizations].sort((a, b) =>
		a.locale === sourceLocale ? -1 : b.locale === sourceLocale ? 1 : a.locale.localeCompare(b.locale),
	)

/** `POST /texts/versions/{versionId}/localizations` body. */
export const addLanguageBody = (values: AddLanguageValues): Record<string, unknown> => ({
	locale: values.locale,
	title: values.title,
	body: values.body,
	body_format: ContentBodyFormat.MARKDOWN,
	translation_origin: values.origin,
	translated_from_id: values.translatedFromId || null,
})

export const addLanguageErrors = (body: Record<string, unknown>): Record<string, string> =>
	schemaErrors(contentLocalizationCreateSchema, body, 'form')

/** `POST /texts/{id}/versions` body. */
export const newVersionBody = (basedOn: string, summary: string): Record<string, unknown> => ({
	based_on_version_id: basedOn,
	change_summary: summary.trim() || null,
})

export const newVersionErrors = (body: Record<string, unknown>): Record<string, string> =>
	schemaErrors(contentVersionCreateSchema, body, 'form')

/** `PUT /texts/localizations/{id}` body (drafts only). */
export const localizationUpdateBody = (draft: DraftValues): Record<string, unknown> => ({
	title: draft.title,
	body: draft.body,
	body_format: ContentBodyFormat.MARKDOWN,
})

export const localizationUpdateErrors = (body: Record<string, unknown>): Record<string, string> =>
	schemaErrors(contentLocalizationUpdateSchema, body, 'form')

export const isDraftDirty = (draft: DraftValues, stored: DraftValues): boolean =>
	draft.title !== stored.title || draft.body !== stored.body

/**
 * "Source changed": the localization was translated from a version older than
 * the newest version that holds a source-language localization.
 */
export const sourceChanged = (
	loc: { locale: string; translatedFromId: string | null },
	versions: readonly ContentVersionDTO[],
	sourceLocale: string,
): boolean => {
	if (loc.locale === sourceLocale || !loc.translatedFromId) return false
	const from = versions.find((v) => v.localizations.some((l) => l.id === loc.translatedFromId))
	if (!from) return false
	const newestSource = Math.max(
		...versions.filter((v) => v.localizations.some((l) => l.locale === sourceLocale)).map((v) => v.versionNumber),
	)
	return from.versionNumber < newestSource
}
